/**
 * Constantes compartilhadas para o core de comunicação WebView2/Corel
 */
export const MAX_PAYLOAD_BYTES = 2_097_152; // 2MB - Suporta nesting com até ~50.000 figuras
export const DEFAULT_REQUEST_TIMEOUT_MS = 15_000; // 15s

/**
 * Timeout de fallback padrão para operações longas (1 hora)
 * 
 * Este valor é usado como fallback quando:
 * 1. A API de timeouts não está disponível
 * 2. O painel admin não tem configuração para a action específica
 * 
 * A prioridade de timeout é:
 * 1. Valor do painel admin (via timeoutService/API)
 * 2. ACTION_TIMEOUTS[action] (fallback por action)
 * 3. DEFAULT_FALLBACK_TIMEOUT_MS (fallback global)
 */
export const DEFAULT_FALLBACK_TIMEOUT_MS = 60 * 60 * 1000; // 1 hora (60 minutos)

/**
 * Timeouts específicos por tipo de ação (em milissegundos)
 * 
 * ⚠️ IMPORTANTE: Estes valores são apenas FALLBACK quando o painel admin não está disponível.
 * A prioridade é sempre buscar do painel admin via timeoutService.
 * 
 * Todos os valores de fallback são 1 hora para garantir que operações longas não sejam
 * interrompidas prematuramente.
 */
export const TIMEOUT_CONFIG = {
  /** Timeout padrão para actions rápidas (app-status, health, etc) */
  DEFAULT: 15_000,                    // 15 segundos
  
  /** Actions de compilação/execução VB (podem processar muitos objetos) */
  VBCOMPILER: 60 * 60 * 1000,         // 1 hora (fallback)
  
  /** Comandos gerais via API */
  COMMAND: 60 * 60 * 1000,            // 1 hora (fallback)
  
  /** Operações com aplicativos externos (Photoshop, etc) */
  EXTERNAL_APP: 60 * 60 * 1000,       // 1 hora (fallback)
  
  /** Salvamento de arquivo (upload, processamento) */
  SAVE_FILE: 60 * 60 * 1000,          // 1 hora (fallback)
  
  /** Abertura de arquivo */
  OPEN_FILE: 60 * 60 * 1000,          // 1 hora (fallback)
} as const;

/**
 * Timeouts por ação (ms) – usado como FALLBACK quando:
 * 1. O painel admin (API) não está disponível
 * 2. A action específica não tem configuração no painel
 * 
 * ⚠️ IMPORTANTE: A prioridade é sempre buscar do painel admin via timeoutService.
 * Estes valores são apenas fallback de segurança.
 * 
 * Todos os valores de fallback são 1 hora (60 * 60 * 1000 = 3.600.000ms)
 * para garantir que operações longas não sejam interrompidas prematuramente.
 */
export const ACTION_TIMEOUTS: Record<string, number> = {
  // ============================================================================
  // Actions de sistema (fallback: 1 hora)
  // ============================================================================
  'app-status': 60 * 60 * 1000,                  // 1h fallback
  'cancel-request': 60 * 60 * 1000,              // 1h fallback
  
  // ============================================================================
  // Actions de monitoramento/foco (fallback: 1 hora)
  // ============================================================================
  'monitorar-objetos': 60 * 60 * 1000,           // 1h fallback
  'focar-objeto-por-staticid': 60 * 60 * 1000,   // 1h fallback
  'abrir-importacao-inteligente': 60 * 60 * 1000,// 1h fallback

  // ============================================================================
  // Actions de lock/documento (fallback: 1 hora)
  // ============================================================================
  'lock-document': 60 * 60 * 1000,               // 1h fallback
  'fechar-documento': 60 * 60 * 1000,            // 1h fallback
  'metadados-objetos': 60 * 60 * 1000,           // 1h fallback

  // ============================================================================
  // Actions de pipeline (fallback: 1 hora)
  // ============================================================================
  'organizar-camada': 60 * 60 * 1000,            // 1h fallback
  'distribuir-por-pagina': 60 * 60 * 1000,       // 1h fallback
  'executar-pipeline': 60 * 60 * 1000,           // 1h fallback

  // ============================================================================
  // Actions de abertura de arquivo (fallback: 1 hora)
  // ============================================================================
  'abrir-arquivo': 60 * 60 * 1000,               // 1h fallback

  // ============================================================================
  // Actions de exportação (fallback: 1 hora)
  // ============================================================================
  'corel-export-batch': 60 * 60 * 1000,          // 1h fallback
  'corel-export-simple': 60 * 60 * 1000,         // 1h fallback
  'exportar-objetos-png-lite': 60 * 60 * 1000,   // 1h fallback
  'exportar-objetos-png': 60 * 60 * 1000,        // 1h fallback

  // ============================================================================
  // Actions externas/guarded (fallback: 1 hora)
  // ============================================================================
  'vbcompiler': 60 * 60 * 1000,                  // 1h fallback
  'salvararquivo': 60 * 60 * 1000,               // 1h fallback
  'coletar-metadados': 60 * 60 * 1000,           // 1h fallback

  // ============================================================================
  // CorelWorks ferramentas (fallback: 1 hora)
  // ============================================================================
  'corelworks:tool': 60 * 60 * 1000,             // 1h fallback
  'corelworks:tool:plan': 60 * 60 * 1000         // 1h fallback
};

/**
 * Ações que não exigem sessionToken na raiz da mensagem
 * 
 * ⚠️ IMPORTANTE: Estas actions são isentas APENAS de sessionToken.
 * A maioria delas AINDA EXIGE HMAC (exceto as que estão em HMAC_EXEMPT_ACTIONS).
 * 
 * Conforme contrato do backend C# (MessageHandlerService.cs):
 * - session:hello - Handshake inicial (gera token)
 * - tools:bootstrap-session - Bootstrap de sessão
 * - debug:* - Actions de debug (qualquer action começando com "debug:")
 *
 * app-status, health:pong e open-tools-docker exigem sessionToken no modo protegido.
 */
export const TOKEN_EXEMPT_ACTIONS = new Set<string>([
  'session:hello',
  'tools:bootstrap-session',
  'tools:ready-for-session',
  'tools:session-applied'
  // debug:* é verificado dinamicamente no código
]);

/**
 * Ações que não exigem HMAC (data.auth)
 * 
 * ⚠️ IMPORTANTE: Estas actions são isentas de HMAC, mas podem exigir sessionToken.
 * 
 * Conforme contrato do backend C# (MessageHandlerService.cs):
 * - health:pong - Health check (exige sessionToken, mas isenta de HMAC)
 * - app-status - Status da aplicação (exige sessionToken, mas isenta de HMAC)
 * - session:hello - Handshake inicial (isenta de HMAC e de sessionToken)
 * - tools:bootstrap-session - Bootstrap de sessão (isenta de HMAC e de sessionToken)
 * - debug:* - Actions de debug (isentas de ambos)
 * 
 * ⚠️ CRÍTICO: open-tools-docker NÃO está nesta lista e também não é isenta de
 * sessionToken no modo protegido.
 */
export const HMAC_EXEMPT_ACTIONS = new Set<string>([
  'app-status',
  'health:pong',
  'session:hello',
  'tools:bootstrap-session',
  'tools:ready-for-session',
  'tools:session-applied'
  // debug:* é verificado dinamicamente no código
]);

/**
 * Ações de longa duração que podem bloquear a thread UI do CorelDRAW
 * 
 * Esta lista deve estar sincronizada com Constants.LongRunningActions do backend C#.
 * 
 * Usada para:
 * - Detecção de status 'busy' durante operações longas
 * - Ajuste de comportamento de verificação de conexão
 * - Timeout continuável (popup de continuação)
 * 
 * Última atualização: 2025-01-XX (sincronizado com backend)
 */
export const LONG_RUNNING_ACTIONS = new Set<string>([
  // Export
  'corel-export-batch',
  'corel-export-simple',
  'exportar-objetos-png-lite',
  'exportar-objetos-png',
  
  // Arquivo
  'abrir-arquivo',
  'salvararquivo',
  
  // Organização
  'organizar-camada',
  'distribuir-por-pagina',
  
  // Metadados
  'metadados-objetos',
  'coletar-metadados',
  
  // Execução de código
  'vbcompiler',
  'execute-vb-code',
  'corel:run-gms',
  
  // CorelWorks
  'corelworks:tool',
  'corelworks:tool:plan'
]);

/**
 * Threshold para marcar como desconectado (em milissegundos)
 * 
 * Se não receber resposta do backend por este período, marca como desconectado.
 * Durante ações longas pendentes, este threshold não é aplicado (status = 'busy').
 */
export const OFFLINE_THRESHOLD_MS = 60_000; // 60 segundos
